/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getFriendlyName,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    IRequestContext
} from '@msdyn365-commerce/core';
import {
    buildCacheKeyWithUrlTokens,
    CurrentCategoryInput,
    getCategoryUrl,
    getCurrentCategory
} from '@msdyn365-commerce-modules/retail-actions';

import { IDefaultPageSummaryConfig } from '../lds-default-summary';
import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
import pageTitles from '../../helpers/utilities/page-titles';
import { getCountryLanguageAsync } from '../../actions/DataActionExtension.g';

/**
 * Category Page Summary Input
 */
export class CategoryPageSummaryInput implements IActionInput {
    public requestContext: IRequestContext;

    public config: IDefaultPageSummaryConfig;

    // public resources: IDefaultPageSummaryResources | undefined;

    constructor(config: IDefaultPageSummaryConfig, requestContext: IRequestContext) {
        this.config = config || {};
        this.requestContext = requestContext;
    }

    public getCacheObjectType = (): string => 'CategoryPageSummary';

    public getCacheKey = (): string => buildCacheKeyWithUrlTokens('CategoryPageSummary', this.requestContext);

    public dataCacheType = (): CacheType => 'request';
}

const createGetCurrentCategoryInput = (inputData: IActionContext): CurrentCategoryInput => {
    if (inputData && inputData.requestContext) {
        return new CurrentCategoryInput(inputData.requestContext);
    }

    throw new Error('Please specify categoryId query string in request.');
};

const action = async (input: CategoryPageSummaryInput, context: IActionContext): Promise<IPageSummaryData> => {
    const { config } = input;
    const ctx = { callerContext: context };
    let currentCategoryResult;
    let countryName;
    const CountryList = await getCountryLanguageAsync(
        ctx,
        context.requestContext.channel?.RecordId || 0,
        context.requestContext.locale
    ).catch(err => {
        console.log(err);
    });

    if (context.requestContext.sitePath && CountryList) {
        countryName = CountryList.find((country: any) => {
            const currentCountry = context.requestContext.sitePath ? context.requestContext.sitePath.split('/') : '';
            if (currentCountry && currentCountry[1]) {
                {
                    return country.countryRegionId === currentCountry[1].toUpperCase();
                }
            } else {
                return;
            }
        });
    }

    let pageTitleStore;
    if (context.requestContext) {
        pageTitleStore = pageTitles.filter((title: any) => {
            return title.language === context.requestContext.locale.toLocaleLowerCase();
        });
    }
    try {
        currentCategoryResult = await getCurrentCategory(createGetCurrentCategoryInput(context), context);
    } catch {
        // Do nothing, if the action fails fallback to values defined in data
    }
    if (currentCategoryResult) {
        const currentCategory = { ...currentCategoryResult };
        let categoryUrl: string | undefined;
        try {
            // Reset category's name neutralizedName
            const localName = getFriendlyName(context.requestContext.locale, currentCategory.NameTranslations);
            const neutralizedName = currentCategory.NeutralizedName || currentCategory.Name;
            currentCategory.Name = localName;
            currentCategory.NeutralizedName = neutralizedName;
            categoryUrl = getCategoryUrl(currentCategory);

            const canonicalDomain = context.requestContext.canonicalDomain;
            if (categoryUrl && canonicalDomain) {
                categoryUrl = `https://${canonicalDomain}${categoryUrl}`.toLocaleLowerCase();
            } else {
                categoryUrl = undefined;
            }
        } catch {
            categoryUrl = undefined;
        }

        return {
            // title: 'TESTING',
            title: `${currentCategory.Name || ''} | ${countryName?.CountryName || ''}${
                pageTitleStore && pageTitleStore[0] ? ` ${pageTitleStore[0].value}` : ''
            }`,
            sharingImageUrl: currentCategory.OfflineImage,
            canonicalUrl: categoryUrl,
            faviconUrl: config && config.faviconUrl
        };

        // If the action fails fallback to values defined from data
    } else if (config) {
        return {
            title: `${config.title || ''} | ${countryName?.CountryName || ''}${
                pageTitleStore && pageTitleStore[0] ? ` ${pageTitleStore[0].value}` : ''
            }`,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/category-page-summary',
    action: <IAction<IPageSummaryData>>action,
    input: (args: ICreateActionContext) => {
        return new CategoryPageSummaryInput(<IDefaultPageSummaryConfig>args.config, args.requestContext);
    }
});
